import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";
import formOptions from "../../config/formOptions.json";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { graphqlEndpoint } from "../../config";
import { READ_AUDIT_QUESTION_BY_ID } from "../../graph/auditQuestions/queries";
import { UPDATE_AUDIT_QUESTION } from "../../graph/auditQuestions/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const auditQuestionSchema = yup.object().shape({
  industry: yup.string().trim().required("Industry is required"),
  area: yup.string().trim().required("Area is required"),
  statement: yup.string().nullable(),
  description: yup.string().nullable(),
  sortOrder: yup.number().integer("Sort order must be a whole number").nullable(),
  isActive: yup.boolean(),
  isHighImpact: yup.boolean(),
});


const EditAuditQuestionsForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setSnackbar } = useSnackbar();

  const [initialValues, setInitialValues] = useState(null);

  useEffect(() => {
    const fetchAuditQuestion = async () => {
      try {
        const { readAuditQuestionByID } = await client.request(
          READ_AUDIT_QUESTION_BY_ID,
          { id }
        );

        setInitialValues({
          id: readAuditQuestionByID.id,
          industry: readAuditQuestionByID.industry || "",
          area: readAuditQuestionByID.area || "",
          statement: readAuditQuestionByID.statement || "",
          description: readAuditQuestionByID.description || "",
          sortOrder:
            readAuditQuestionByID.sortOrder === null ||
            readAuditQuestionByID.sortOrder === undefined
              ? ""
              : String(readAuditQuestionByID.sortOrder),
          isActive: Boolean(readAuditQuestionByID.isActive),
          isHighImpact: Boolean(readAuditQuestionByID.isHighImpact),
        });
      } catch (err) {
        console.error("Failed to load audit question:", err);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load audit question",
        });
      }
    };

    fetchAuditQuestion();
  }, [id, setSnackbar]);

  const handleSubmit = async (values) => {
    try {
      const input = {
        id: values.id,
        industry: values.industry,
        area: values.area,
        statement: values.statement || null,
        description: values.description || null,
        sortOrder: values.sortOrder === "" ? null : Number(values.sortOrder),
        isActive: values.isActive,
        isHighImpact: values.isHighImpact,
      };

      await client.request(UPDATE_AUDIT_QUESTION, { input });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Audit question updated successfully",
      });

      navigate("/manageAuditQuestions");
    } catch (err) {
      console.error("Update failed:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message:
          err?.response?.errors?.[0]?.message ||
          "Failed to update audit question",
      });
    }
  };

  if (!initialValues) return <div>Loading...</div>;

  return (
    <Box>
      <Header
        title="EDIT AUDIT QUESTION"
        subtitle={`Edit ${initialValues.title}`}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={auditQuestionSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Industry</InputLabel>
                <Select
                  name="industry"
                  value={values.industry}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Industry"
                >
                  {formOptions.industryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Area</InputLabel>
                <Select
                  name="area"
                  value={values.area}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Area"
                >
                  {formOptions.operationalAreaOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Sort Order"
                name="sortOrder"
                type="number"
                value={values.sortOrder}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.sortOrder && !!errors.sortOrder}
                helperText={touched.sortOrder && errors.sortOrder}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Statement"
                name="statement"
                value={values.statement}
                onChange={handleChange}
                onBlur={handleBlur}
                multiline
                minRows={3}
                sx={{ gridColumn: "span 4" }}
              />

              <TextField
                label="Description"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                multiline
                minRows={4}
                sx={{ gridColumn: "span 4" }}
              />

              <Box sx={{ gridColumn: "span 4" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.isActive}
                      onChange={(event) =>
                        setFieldValue("isActive", event.target.checked)
                      }
                      color="secondary"
                    />
                  }
                  label="Active"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={values.isHighImpact}
                      onChange={(event) =>
                        setFieldValue("isHighImpact", event.target.checked)
                      }
                      color="secondary"
                    />
                  }
                  label="High Impact"
                />
              </Box>
            </Box>

            <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
              <ActionButton
                tone="ghost"
                onClick={() => navigate("/manageAuditQuestions")}
              >
                Cancel
              </ActionButton>

              <ActionButton type="submit" tone="sucess">
                Save Changes
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default EditAuditQuestionsForm;